import { useState, useEffect } from 'react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  MoreVertical,
  Upload,
  Download,
  Eye,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Shield
} from 'lucide-react';
import { procurementService, VendorDocument, VendorDocumentCreate, VendorDocumentUpdate, VendorDocumentType } from '../../../src/services/procurement.service';
import { documentService } from '../../../src/services/document.service';
import { authService } from '../../../src/services/auth.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';

interface VendorDocumentsProps {
  vendorId: string;
  vendorName: string;
}

interface DocumentModalProps {
  mode: 'add' | 'edit';
  vendorId: string;
  document?: VendorDocument;
  trigger: React.ReactNode;
  onSuccess: () => void;
}

function DocumentModal({ mode, vendorId, document: vendorDocument, trigger, onSuccess }: DocumentModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    document_name: vendorDocument?.document_name || '',
    document_type: vendorDocument?.document_type || 'other' as VendorDocumentType,
    document_number: vendorDocument?.document_number || '',
    description: vendorDocument?.description || '',
    issue_date: vendorDocument?.issue_date?.split('T')[0] || '',
    expiry_date: vendorDocument?.expiry_date?.split('T')[0] || ''
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const documentTypes: { value: VendorDocumentType; label: string }[] = [
    { value: 'insurance', label: 'Insurance' },
    { value: 'license', label: 'License' },
    { value: 'certification', label: 'Certification' },
    { value: 'contract', label: 'Contract' },
    { value: 'w9', label: 'W-9 Form' },
    { value: 'reference', label: 'Reference' },
    { value: 'other', label: 'Other' }
  ];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.document_name.trim()) {
      newErrors.document_name = 'Document name is required';
    }
    if (mode === 'add' && !selectedFile) {
      newErrors.file = 'Please select a file to upload';
    }
    if (formData.issue_date && formData.expiry_date && formData.issue_date >= formData.expiry_date) {
      newErrors.expiry_date = 'Expiry date must be after issue date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError({
        title: 'Validation Error',
        message: 'Please fill in all required fields correctly'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let fileId: string | undefined;
      let objectKey: string | undefined;
      let fileName: string | undefined;
      let fileSize: number | undefined;
      let mimeType: string | undefined;

      // Get user profile for organization_id
      const userProfile = await authService.getUserProfile();
      if (!userProfile?.organizationId) {
        throw new Error('Organization ID not found');
      }

      // Upload file if selected
      if (selectedFile) {
        const uploadResponse = await documentService.uploadFile(selectedFile, userProfile.organizationId);
        if (uploadResponse.success && uploadResponse.data) {
          fileId = uploadResponse.data.storage_id;
          objectKey = uploadResponse.data.storage_id;
          fileName = selectedFile.name;
          fileSize = selectedFile.size;
          mimeType = selectedFile.type;
        }
      }

      const documentData = {
        organization_id: userProfile.organizationId,
        vendor_id: vendorId,
        document_name: formData.document_name,
        document_type: formData.document_type,
        document_number: formData.document_number || undefined,
        description: formData.description || undefined,
        issue_date: formData.issue_date || undefined,
        expiry_date: formData.expiry_date || undefined,
        ...(fileId && {
          file_id: fileId,
          object_key: objectKey,
          file_name: fileName,
          file_size: fileSize,
          mime_type: mimeType
        })
      };

      if (mode === 'add') {
        await procurementService.createVendorDocument(vendorId, documentData as VendorDocumentCreate);
        showSuccess({
          title: 'Document Added',
          message: `${formData.document_name} has been uploaded successfully`
        });
      } else if (mode === 'edit' && vendorDocument) {
        await procurementService.updateVendorDocument(vendorDocument.document_id, documentData as VendorDocumentUpdate);
        showSuccess({
          title: 'Document Updated',
          message: `${formData.document_name} has been updated successfully`
        });
      }

      onSuccess();
    } catch (error: any) {
      console.error('Failed to save document:', error);
      showError({
        title: mode === 'add' ? 'Failed to Add Document' : 'Failed to Update Document',
        message: error?.message || 'An unexpected error occurred'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {mode === 'add' ? 'Upload Document' : 'Edit Document'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add' ? 'Upload a new document for this vendor' : 'Update document information'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="document-name">Document Name *</Label>
            <Input
              id="document-name"
              value={formData.document_name}
              onChange={(e) => setFormData({...formData, document_name: e.target.value})}
              placeholder="General Liability Insurance 2024"
            />
            {errors.document_name && <p className="text-sm text-destructive">{errors.document_name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="document-type">Document Type</Label>
              <Select
                value={formData.document_type}
                onValueChange={(value) => setFormData({...formData, document_type: value as VendorDocumentType})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="document-number">Document Number</Label>
              <Input
                id="document-number"
                value={formData.document_number}
                onChange={(e) => setFormData({...formData, document_number: e.target.value})}
                placeholder="POL-2024-12345"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Additional details about this document..."
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="issue-date">Issue Date</Label>
              <Input
                id="issue-date"
                type="date"
                value={formData.issue_date}
                onChange={(e) => setFormData({...formData, issue_date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expiry-date">Expiry Date</Label>
              <Input
                id="expiry-date"
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
              />
              {errors.expiry_date && <p className="text-sm text-destructive">{errors.expiry_date}</p>}
            </div>
          </div>

          {mode === 'add' && (
            <div className="space-y-2">
              <Label htmlFor="file-upload">File Upload *</Label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => document.getElementById('file-upload')?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Browse
                </Button>
                <div className="flex-1">
                  <Input
                    id="file-upload"
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    className="hidden"
                  />
                  <Input
                    type="text"
                    value={selectedFile?.name || 'No file chosen'}
                    readOnly
                    className="bg-muted cursor-default"
                    placeholder="No file chosen"
                  />
                </div>
              </div>
              {errors.file && <p className="text-sm text-destructive">{errors.file}</p>}
              <p className="text-xs text-muted-foreground">
                Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild><Button variant="outline" disabled={isSubmitting}>Cancel</Button></DialogClose>
          <Button onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : mode === 'add' ? 'Upload Document' : 'Update Document'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VendorDocuments({ vendorId, vendorName }: VendorDocumentsProps) {
  const [documents, setDocuments] = useState<VendorDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      console.log('[VendorDocuments] Fetching documents for vendorId:', vendorId);
      console.log('[VendorDocuments] Type filter:', typeFilter);
      const documentType = typeFilter === 'all' ? undefined : typeFilter;
      const response = await procurementService.getVendorDocuments(vendorId, documentType);
      console.log('[VendorDocuments] API Response:', response);
      console.log('[VendorDocuments] Response.data:', response.data);
      const documentsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      console.log('[VendorDocuments] Documents loaded:', documentsData.length, documentsData);
      setDocuments(documentsData);
    } catch (error) {
      console.error('[VendorDocuments] Failed to fetch documents:', error);
      showError({
        title: 'Failed to Load Documents',
        message: 'Unable to load vendor documents'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [vendorId, typeFilter]);

  const handleDelete = async (document: VendorDocument) => {
    try {
      await procurementService.updateVendorDocument(document.document_id, {});
      showSuccess({
        title: 'Document Deleted',
        message: `${document.document_name} has been removed`
      });
      fetchDocuments();
    } catch (error: any) {
      showError({
        title: 'Failed to Delete Document',
        message: error?.message || 'An unexpected error occurred'
      });
    }
  };

  const handleVerify = async (document: VendorDocument) => {
    try {
      await procurementService.verifyVendorDocument(document.document_id, 'Verified by system');
      showSuccess({
        title: 'Document Verified',
        message: `${document.document_name} has been verified`
      });
      fetchDocuments();
    } catch (error: any) {
      showError({
        title: 'Failed to Verify Document',
        message: error?.message || 'An unexpected error occurred'
      });
    }
  };

  const getDocumentTypeBadge = (type: VendorDocumentType) => {
    const colors: Record<VendorDocumentType, string> = {
      insurance: 'bg-blue-100 text-blue-800 border-blue-200',
      license: 'bg-green-100 text-green-800 border-green-200',
      certification: 'bg-purple-100 text-purple-800 border-purple-200',
      contract: 'bg-orange-100 text-orange-800 border-orange-200',
      w9: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      reference: 'bg-pink-100 text-pink-800 border-pink-200',
      other: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return <Badge className={colors[type]}>{type.replace('_', ' ').toUpperCase()}</Badge>;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading documents...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Documents</h3>
          <p className="text-sm text-muted-foreground">Manage documents for {vendorName}</p>
        </div>
        <div className="flex gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="insurance">Insurance</SelectItem>
              <SelectItem value="license">License</SelectItem>
              <SelectItem value="certification">Certification</SelectItem>
              <SelectItem value="contract">Contract</SelectItem>
              <SelectItem value="w9">W-9</SelectItem>
            </SelectContent>
          </Select>
          <DocumentModal
            mode="add"
            vendorId={vendorId}
            onSuccess={fetchDocuments}
            trigger={
              <Button size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Upload Document
              </Button>
            }
          />
        </div>
      </div>

      {documents.length === 0 ? (
        <div className="text-center py-12 border rounded-lg">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No documents yet</h3>
          <p className="text-muted-foreground mb-4">Upload documents like insurance, licenses, and certifications</p>
          <DocumentModal
            mode="add"
            vendorId={vendorId}
            onSuccess={fetchDocuments}
            trigger={
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Upload First Document
              </Button>
            }
          />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Document</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Validity</TableHead>
              <TableHead>Verification</TableHead>
              <TableHead>File Info</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc.document_id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{doc.document_name}</p>
                    {doc.document_number && (
                      <p className="text-sm text-muted-foreground">{doc.document_number}</p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {getDocumentTypeBadge(doc.document_type)}
                </TableCell>
                <TableCell>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      <span className="text-muted-foreground">Issued:</span>
                      {formatDate(doc.issue_date)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      <span className="text-muted-foreground">Expires:</span>
                      {formatDate(doc.expiry_date)}
                      {doc.expires_soon && (
                        <Badge variant="outline" className="text-orange-600 border-orange-600 ml-2">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Expiring
                        </Badge>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {doc.is_verified ? (
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      <Shield className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-orange-600 border-orange-600">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Unverified
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {doc.file_name && <p className="truncate max-w-32">{doc.file_name}</p>}
                    {doc.file_size && <p className="text-muted-foreground">{formatFileSize(doc.file_size)}</p>}
                  </div>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </DropdownMenuItem>
                      {!doc.is_verified && (
                        <DropdownMenuItem onClick={() => handleVerify(doc)}>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Verify Document
                        </DropdownMenuItem>
                      )}
                      <DocumentModal
                        mode="edit"
                        vendorId={vendorId}
                        document={doc}
                        onSuccess={fetchDocuments}
                        trigger={
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        }
                      />
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem className="text-destructive" onSelect={(e) => e.preventDefault()}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Document</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete {doc.document_name}? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(doc)} className="bg-destructive hover:bg-destructive/90">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
